import { For, Show, createEffect, createMemo, createSignal, onMount, splitProps } from "solid-js";
import { directives$ } from "../directives";
//
import type { MainProps } from "./_model";
import ArrowIcon from "./assets/icons/Vector.svg";

function isOutOfViewport(elem) {
  // Get element's bounding
  var bounding = elem.getBoundingClientRect();

  // Check if it's out of the viewport on each side
  var out = {} as any;
  out.top = bounding.top < 0;
  out.left = bounding.left < 0;
  out.bottom = bounding.bottom > (window.innerHeight || document.documentElement.clientHeight);
  out.right = bounding.right > (window.innerWidth || document.documentElement.clientWidth);
  out.any = out.top || out.left || out.bottom || out.right;
  out.all = out.top && out.left && out.bottom && out.right;

  return out;
}

const EMPTY_OPTION = "undefined";
export default function Select(props: MainProps) {
  const [local, others] = splitProps(props, [
    "options",
    "disabled",
    "placeholder",
    "initialValue",
    "selectedClass",
    "selectedGroupClass",
    "iconClass",
    "optionClass",
    "optionGroupClass",
    "multiple",
    "placeholderClass",
    "onClick",
    "onselected",
    "onSelected",
    "onNoOptions",
    "allowClearSelected",
    "clearSelectedClass",
    "emptyOptionAlt",
  ]);
  // @ts-ignore: unused function 'clickOutside'
  const { clickOutside } = directives$.events;
  const empty_option = local.emptyOptionAlt ?? EMPTY_OPTION;
  const placeholder = local.placeholder ?? "Select...";
  const [$triggers, setTriggers] = createSignal({ disabled: local.disabled, touched: false, blurred: false });
  const [$selected, setSelected] = createSignal([] as any[]);
  const $user_selected = createMemo(() => $selected().length > 0);
  const $show_options = createMemo(() => $triggers().touched);
  const [$options_direction, setOptionsDirection] = createSignal("down" as "up" | "down");
  let options_ref = undefined as HTMLDivElement;

  // createEffect(() => {
  //   const value = $triggers();
  //   console.log("select triggers :: ", value);
  // });
  createEffect(() => {
    const show_options = $show_options();
    if (!show_options) {
      setOptionsDirection("down");
      return;
    }
    const isOut = isOutOfViewport(options_ref);
    // console.log("is out :: ", options_ref, " :: ", isOut);
    if (isOut.any) {
      // At least one side of the element is out of viewport
      // console.log("element ::", options_ref, " :: is out");
      setOptionsDirection("up");
    }
  });

  createEffect(() => {
    if (local.initialValue != undefined) {
      setSelected([local.initialValue]);
    } else {
      setSelected([]);
    }
  });

  function selectOption(e: Event, idx: number, empty_option: boolean) {
    e.preventDefault();
    // check if idx is for the allow clear selected, meaning equals null
    const value = empty_option ? EMPTY_OPTION : local.options[idx];
    let new_selected = undefined as any[];
    if (local.multiple) {
      const selected = $selected();
      const value_exist = selected.indexOf(value);
      if (value_exist) {
        new_selected = selected.filter((item) => item !== value);
      } else {
        new_selected = [...selected, value];
      }
    } else {
      new_selected = [value];
    }
    setSelected(new_selected);
    local.onselected?.(new_selected);
    local.onSelected?.(new_selected);
  }

  return (
    <section
      {...others}
      class={`${others.class ?? ""} relative flex flex-col cursor-pointer `}
      use:clickOutside={() => {
        if ($triggers().touched) {
          setTriggers((s) => ({ ...s, touched: false }));
        }
      }}
      classList={{
        // invalid: !!_control.errors,
        touched: $show_options(),
        disabled: $triggers().disabled,
        ...others.classList,
      }}
      onClick={(e) => setTriggers((s) => ({ ...s, touched: !s.touched }))}
      onblur={() => setTriggers((s) => ({ ...s, blurred: true }))}
      onfocus={() => setTriggers((s) => ({ ...s, touched: true }))}
      onfocusin={() => setTriggers((s) => ({ ...s, touched: true }))}
      onfocusout={() => setTriggers((s) => ({ ...s, touched: false }))}
    >
      {/* user selection and arrow icon  */}
      <div class="relative grid grid-cols-[1fr_10%]  w-full h-full gap-1 items-center justify-between px-2 overfl">
        <div
          class={`selected-group relative flex flex-row w-full h-full overflow-hidden ${
            local.selectedGroupClass ?? ""
          }`}
        >
          {!$user_selected() ? (
            <span class={`flex w-full h-full placeholder ${local.placeholderClass ?? ""}`}>{placeholder}</span>
          ) : local.multiple == undefined ? (
            <span class={`whitespace-nowrap selected  ${local.selectedClass ?? ""}`}>
              {$selected()[0] === EMPTY_OPTION ? empty_option : String($selected()[0])}
            </span>
          ) : (
            <For each={$selected()}>
              {(item, index) => {
                // const arr_length = $selected().length;

                return <span class={`selected ${local.selectedClass ?? ""}`}>{item}</span>;
              }}
            </For>
          )}
        </div>
        <ArrowIcon class={`w-15px max-w-15px h-15px duration-300   ${$show_options() ? "!rotate-180" : ""}`} />
      </div>
      {/* options */}
      <div
        ref={(ref) => {
          if (ref == undefined) {
            return;
          }
          options_ref = ref;
        }}
        style={{
          height: "fit-content",
          color: "black",
          "background-color": "white",
          // border: "solid 2px gray",
        }}
        class={
          "option-group flex flex-col w-full overflow-y-auto self-center" +
          " " +
          ($show_options() ? "absolute" : "!hidden") +
          " " +
          ($options_direction() === "down" ? "top-106%" : "bottom-106%") +
          " " +
          (local.optionGroupClass ?? " ")
        }
      >
        <Show when={$show_options()}>
          <Show when={local.allowClearSelected != undefined}>
            <span
              class={`cursor-pointer option option-clear ${local.optionClass ?? ""} ${local.clearSelectedClass ?? ""}`}
              onclick={(e) => selectOption(e, null, false)}
            >
              {typeof local.allowClearSelected === "string" ? local.allowClearSelected : "Clear"}
            </span>
          </Show>
          <For each={local.options}>
            {(item, index) => {
              const i = index();
              const empty_item = item == undefined || (typeof item === "string" && item.length <= 0);
              const $chosen = createMemo(() => $selected().indexOf(item) >= 0);
              return (
                <span
                  class={`cursor-pointer option ${local.optionClass != undefined ? local.optionClass : ""} ${
                    $chosen() ? "chosen" : ""
                  }`}
                  onclick={(e) => selectOption(e, i, empty_item)}
                >
                  {empty_item ? empty_option : item}
                </span>
              );
            }}
          </For>
        </Show>
      </div>
    </section>
  );
}
